import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { useLocation } from 'preact-iso';
import { getWebTypeMeta } from '@/constants/webTypes';
import { LeadGoalBar } from '@/components';
import { formatDateOnly } from '@/utils/dateTimeFormatters';
import { shortAgo } from '@/utils/time';
import { routes } from '@/utils/routes';
import { DataGrid } from '@/components/common/grids';
const getLaunchStatus = (launchDate) => {
    if (!launchDate)
        return { text: '—', variant: 'past' };
    // Get today's date as YYYY-MM-DD in local time
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    if (launchDate === todayStr) {
        return { text: 'Today', variant: 'today' };
    }
    else if (launchDate < todayStr) {
        return { text: formatDateOnly(launchDate), variant: 'past' };
    }
    else {
        return { text: formatDateOnly(launchDate), variant: 'future' };
    }
};
export function CampaignWebsListView({ webs, campaignId, workspaceId, clientCompanyId }) {
    const location = useLocation();
    const handleRowClick = (web) => {
        const url = routes.scoped(clientCompanyId, workspaceId).web(campaignId, web.Id);
        location.route(url);
    };
    const columns = [
        {
            key: 'title',
            header: 'Web Title',
            sortable: true,
            accessor: (web) => web.Title,
            render: (web) => (_jsx("div", { className: "font-medium text-slate-900 dark:text-slate-100", children: web.Title })),
        },
        {
            key: 'type',
            header: 'Type',
            sortable: true,
            accessor: (web) => web.WebType.toLowerCase(),
            render: (web) => {
                const typeMeta = getWebTypeMeta(web.WebType);
                const Icon = typeMeta.Icon;
                const chipClass = typeMeta.chipClass;
                const displayLabel = web.WebType.trim();
                return (_jsxs("span", { className: `inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full ${chipClass}`, children: [_jsx(Icon, { className: "w-3.5 h-3.5" }), displayLabel] }));
            },
        },
        {
            key: 'launchDate',
            header: 'Launch Date',
            sortable: true,
            accessor: (web) => new Date(web.LaunchDate).getTime(),
            render: (web) => {
                const launchStatus = getLaunchStatus(web.LaunchDate);
                return (_jsx("div", { className: `text-sm ${launchStatus.variant === 'today'
                        ? 'text-primary-600 dark:text-primary-400 font-semibold'
                        : launchStatus.variant === 'future'
                            ? 'text-slate-600 dark:text-slate-400'
                            : 'text-slate-500 dark:text-slate-500'}`, children: launchStatus.text }));
            },
        },
        {
            key: 'leads',
            header: 'Leads',
            sortable: true,
            accessor: (web) => web.LeadsCount || 0,
            render: (web) => {
                const goal = Math.max(0, web.LeadGoal || 0);
                const leads = Math.max(0, web.LeadsCount || 0);
                return (_jsxs("div", { className: "text-sm font-medium text-slate-900 dark:text-slate-100", children: [leads.toLocaleString(), " / ", goal.toLocaleString()] }));
            },
        },
        {
            key: 'progress',
            header: 'Progress',
            sortable: false,
            render: (web) => {
                const goal = Math.max(0, web.LeadGoal || 0);
                const leads = Math.max(0, web.LeadsCount || 0);
                return (_jsx("div", { className: "w-32", children: _jsx(LeadGoalBar, { sourced: leads, goal: goal }) }));
            },
        },
        {
            key: 'lastLead',
            header: 'Last Lead',
            sortable: true,
            accessor: (web) => web.LastLeadCreatedDate ? new Date(web.LastLeadCreatedDate).getTime() : 0,
            render: (web) => (_jsx("div", { className: "text-sm text-slate-600 dark:text-slate-400", children: web.LastLeadCreatedDate ? shortAgo(web.LastLeadCreatedDate) : 'No leads' })),
        },
    ];
    const getSortComparator = (key) => {
        return (a, b) => {
            const column = columns.find(col => col.key === key);
            if (!column?.accessor)
                return 0;
            const aValue = column.accessor(a);
            const bValue = column.accessor(b);
            // Handle null/undefined - sort to the end
            if (aValue == null && bValue == null)
                return 0;
            if (aValue == null)
                return 1;
            if (bValue == null)
                return -1;
            if (aValue < bValue)
                return -1;
            if (aValue > bValue)
                return 1;
            return 0;
        };
    };
    return (_jsx(DataGrid, { data: webs, columns: columns, getRowKey: (web) => web.Id, onRowClick: handleRowClick, getSortComparator: getSortComparator, defaultSortKey: "launchDate", defaultSortDirection: "desc", emptyMessage: "No webs found", pagination: true, pageSize: 10 }));
}
